/**
 * Appointments API — reads via view proxy, writes via hand-coded endpoints.
 *
 * Read:
 *   GET    /v1/views/appointments    — list (via view proxy)
 *
 * Write (hand-coded, kept as-is):
 *   POST   /v1/appointments          — create
 *   PATCH  /v1/appointments/{id}     — update
 *   DELETE /v1/appointments/{id}     — soft-cancel (sets status=cancelled)
 */

import { get, post, patch, del } from '../http/serviceLayerClient'

// ── Types — match v_appointments VIEW columns ─────────────────────────

export interface Appointment {
  id: string
  owner_email: string
  org_id: string | null
  time_type: string | null
  starts_at: string
  ends_at: string
  time_label: string | null
  date_target: string | null
  lead_id: string | null
  contact_person_id: string | null
  location_text: string | null
  commitment: string | null
  status: string
  notes: string | null
  created_at: string
  updated_at: string
}

export interface CreateAppointmentRequest {
  owner_email: string
  lead_id?: string | null
  title: string
  description?: string | null
  start_time: string
  end_time: string
  location?: string | null
}

export interface UpdateAppointmentRequest {
  title?: string
  description?: string | null
  status?: 'scheduled' | 'completed' | 'cancelled'
  start_time?: string
  end_time?: string
  location?: string | null
}

export interface ListAppointmentsParams {
  owner_email?: string
  lead_id?: string
  status?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}

// ── API Functions ──────────────────────────────────────────────────────

export async function createAppointment(data: CreateAppointmentRequest): Promise<Appointment> {
  const result = await post<Appointment>('/v1/appointments', data)
  return result.data
}

export async function listAppointments(params?: ListAppointmentsParams): Promise<Appointment[]> {
  const query = new URLSearchParams()
  if (params) {
    for (const [key, val] of Object.entries(params)) {
      if (val != null) query.set(key, String(val))
    }
  }
  const qs = query.toString()
  const result = await get<Appointment[]>(`/v1/views/appointments${qs ? `?${qs}` : ''}`)
  return result.data
}

export async function getAppointment(id: string): Promise<Appointment> {
  const result = await get<Appointment>(`/v1/views/appointments?id=eq.${encodeURIComponent(id)}&limit=1`)
  const items = result.data as unknown as Appointment[]
  return Array.isArray(items) ? items[0] : result.data
}

export async function updateAppointment(id: string, data: UpdateAppointmentRequest): Promise<Appointment> {
  const result = await patch<Appointment>(`/v1/appointments/${id}`, data)
  return result.data
}

export async function cancelAppointment(id: string): Promise<void> {
  await del<void>(`/v1/appointments/${id}`)
}
